import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, FileText, ChevronDown, Trash2, Ticket, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';

// --- OPTIMIZED BET ITEM COMPONENT (UI REFACTOR) ---
const BetItem = React.memo(({ bet, onRemove, onStakeChange, stake, isConflict, activeTab, currencySymbol, showInput }) => {
    return (
        <div className={`relative bg-[#1A1A1A] rounded-xl overflow-hidden border transition-all shadow-sm group ${isConflict ? 'border-red-500/50' : 'border-white/5 hover:border-white/10'}`}>

            {/* Header Line - Compact */}
            <div className={`px-3 py-2.5 flex justify-between items-start ${isConflict ? 'bg-red-500/10' : ''}`}>
                <div className="w-[85%] space-y-0.5">
                    <div className="flex items-center gap-1.5">
                        {bet.mode === 'bet_builder' && (
                            <div className="flex items-center gap-0.5 bg-[#F59E0B] text-black px-1.5 py-0.5 rounded-[4px] shadow-sm">
                                <Ticket size={8} strokeWidth={3} />
                                <span className="text-[7px] font-black tracking-wider">BUILDER</span>
                            </div>
                        )}
                        <span className={`text-[9px] font-black uppercase tracking-wider truncateWidth ${isConflict ? 'text-red-400' : 'text-[#F59E0B]'}`}>{bet.marketName}</span>
                    </div>
                    <div className={`font-black text-sm md:text-base leading-tight ${isConflict ? 'text-red-200' : 'text-white'}`}>{bet.selectionText}</div>
                    <div className="text-[9px] text-gray-400 truncate font-medium mt-0.5">{bet.home} vs {bet.away}</div>
                </div>
                {/* Close Button UI from reference */}
                <button onClick={() => onRemove(bet.selectionId)} className="text-white/20 hover:text-white bg-white/5 hover:bg-white/10 p-1.5 rounded-full transition-colors active:scale-95">
                    <X size={14} strokeWidth={3} />
                </button>
            </div>

            {/* Footer / Input Line - Compact */}
            <div className={`bg-[#151515] px-3 py-2 flex justify-between items-center border-t ${isConflict ? 'border-red-500/20' : 'border-white/5'}`}>
                {/* Odd Pill */}
                <div className="flex items-center h-8 bg-[#000000]/40 px-3 rounded-lg border border-white/5">
                    <span className="text-[8px] font-bold text-gray-500 uppercase tracking-wider mr-2">CUOTA</span>
                    <span className={`font-black text-sm font-mono ${isConflict ? 'text-red-400 line-through opacity-70' : 'text-white'}`}>
                        {parseFloat(bet.oddValue).toFixed(2)}
                    </span>
                </div>

                {/* Simple Mode Input per Card */}
                {showInput && (
                    <div className="flex items-center w-24 md:w-28 relative group h-8">
                        <div className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                            <span className="text-[10px] font-bold text-[#F59E0B]">{currencySymbol}</span>
                        </div>
                        <input
                            type="number"
                            inputMode="decimal"
                            placeholder="Monto"
                            value={stake || ''}
                            onChange={(e) => onStakeChange(bet.selectionId, e.target.value)}
                            className="w-full h-full bg-[#202020] border border-white/10 focus:border-[#F59E0B] rounded-lg pl-7 pr-2 text-right text-xs text-white font-bold outline-none transition-all placeholder:text-gray-600 focus:bg-[#252525]"
                        />
                    </div>
                )}
            </div>
            {isConflict && <div className="absolute inset-0 border-2 border-red-500/40 rounded-xl pointer-events-none animate-pulse"></div>}
        </div>
    );
});

export const BetSlip = ({ betSlip, setBetSlip, onPlaceBet, user, userProfile, activeCurrency, onCloseMobile }) => {
    // UI State
    const [isMinimized, setIsMinimized] = useState(true);
    const [activeTab, setActiveTab] = useState('simple');
    const [isPlacing, setIsPlacing] = useState(false);
    const [keepInCoupon, setKeepInCoupon] = useState(true);

    // Minimize logic
    const toggleMinimize = (e) => {
        e?.stopPropagation();
        if (!onCloseMobile) setIsMinimized(prev => !prev);
    };

    // Determine Logic for Desktop vs Mobile
    const isMobileModal = !!onCloseMobile;
    const desktopClasses = `hidden lg:flex flex-col fixed bottom-0 right-6 z-[100] w-[360px] bg-[#121212] rounded-t-2xl border-x border-t border-white/10 shadow-[0_-10px_60px_rgba(0,0,0,0.8)] transition-all duration-300 ease-in-out overflow-hidden font-sans`;
    const mobileClasses = `flex flex-col h-full bg-[#121212] w-full font-sans`;

    // Dynamic Height
    const styles = !isMobileModal ? {
        height: isMinimized ? '56px' : 'min(700px, 90vh)',
    } : {};

    // Stakes State
    const [combinedStake, setCombinedStake] = useState('');
    const [simpleStakes, setSimpleStakes] = useState({});

    const selections = useMemo(() => Array.from(betSlip.values()), [betSlip]);

    // --- MODE & CONFLICT DETECTION ---
    const { hasBuilder, isMixed, isBuilderInvalid, conflictingEventIds, hasErrors } = useMemo(() => {
        const modes = selections.map(s => s.mode || 'standard');
        const hasBuilder = modes.includes('bet_builder');
        const builderCount = selections.filter(s => s.mode === 'bet_builder').length;
        const isMixed = hasBuilder && modes.includes('standard');
        const isBuilderInvalid = hasBuilder && builderCount < 2 && !isMixed;

        const eventCounts = {};
        selections.forEach(s => {
            eventCounts[s.eventId] = (eventCounts[s.eventId] || 0) + 1;
        });
        const conflictingEventIds = Object.keys(eventCounts).filter(id => eventCounts[id] > 1);

        const hasErrors = isMixed || isBuilderInvalid || (activeTab === 'combined' && !hasBuilder && conflictingEventIds.length > 0);

        return { hasBuilder, isMixed, isBuilderInvalid, conflictingEventIds, hasErrors };
    }, [selections, activeTab]);

    // Derived state for UI Conflict
    const getIsConflict = useCallback((selection) => {
        if (activeTab === 'simple') return false;
        if (selection.mode === 'bet_builder') return false;
        if (activeTab === 'combined' && conflictingEventIds.includes(String(selection.eventId))) return true;
        return false;
    }, [activeTab, conflictingEventIds]);

    useEffect(() => {
        if (hasBuilder && activeTab !== 'combined') setActiveTab('combined');
    }, [hasBuilder, activeTab]);

    // Helpers
    const currencySymbol = activeCurrency === 'USDT' ? '₮' : 'Bs';

    const formatCurrency = (val) => {
        return `${currencySymbol} ${parseFloat(val || 0).toFixed(2)}`;
    };

    // Calculate Returns
    const potentialWin = useMemo(() => {
        if (activeTab === 'simple') {
            return selections.reduce((acc, s) => {
                const stake = parseFloat(simpleStakes[s.selectionId] || 0);
                return acc + (stake * s.oddValue);
            }, 0);
        }
        if (activeTab === 'combined') {
            if (hasErrors) return 0;
            const validSelections = selections.filter(s => !getIsConflict(s));
            if (validSelections.length === 0) return 0;
            const totalOdds = validSelections.reduce((acc, s) => acc * s.oddValue, 1);
            return (parseFloat(combinedStake || 0) * totalOdds);
        }
        return 0;
    }, [activeTab, selections, simpleStakes, combinedStake, hasErrors, getIsConflict]);

    // Handlers
    const handleStakeChange = useCallback((id, val) => {
        if (val.length < 10) setSimpleStakes(prev => ({ ...prev, [id]: val }));
    }, []);

    const removeBet = useCallback((id) => {
        setBetSlip(prev => {
            const newSlip = new Map(prev);
            newSlip.delete(id);
            if (newSlip.size === 0 && onCloseMobile) onCloseMobile();
            return newSlip;
        });
        setSimpleStakes(prev => {
            const newSimple = { ...prev };
            delete newSimple[id];
            return newSimple;
        });
    }, [setBetSlip, onCloseMobile]);

    const handlePlace = async () => {
        if (hasErrors) return toast.error("Por favor corrige los conflictos en tu cupón.");
        if (!user) return toast.warn("Inicia sesión para apostar");

        // Logic placeholder for place bet
        const isUSDT = activeCurrency === 'USDT';
        const type = isUSDT ? 'usdt' : (activeCurrency === 'BONUS' ? 'bonus' : 'bs');
        const balance = isUSDT ? parseFloat(userProfile?.balanceUsdt || 0) : parseFloat(userProfile?.balanceBs || 0);

        setIsPlacing(true);
        const timeoutId = setTimeout(() => {
            if (isPlacing) {
                setIsPlacing(false);
                toast.error("La solicitud tardó demasiado.");
            }
        }, 12000);

        try {
            let totalReq = 0;
            let finalBets = [];

            if (activeTab === 'simple') {
                finalBets = selections
                    .map(s => ({ ...s, stake: parseFloat(simpleStakes[s.selectionId] || 0) }))
                    .filter(s => s.stake > 0);
                if (finalBets.length === 0) throw new Error("Ingresa al menos un monto.");
                totalReq = finalBets.reduce((a, b) => a + b.stake, 0);

            } else if (activeTab === 'combined') {
                const stake = parseFloat(combinedStake || 0);
                if (stake <= 0) throw new Error("Ingresa el monto de apuesta.");
                totalReq = stake;
                finalBets = selections;
            }

            if (totalReq > balance) throw new Error("Saldo insuficiente.");

            if (activeTab === 'combined') {
                const totalOdds = selections.reduce((acc, s) => acc * s.oddValue, 1);
                await onPlaceBet(combinedStake, totalOdds, type, selections);
            } else if (activeTab === 'simple') {
                await Promise.all(finalBets.map(b => onPlaceBet(b.stake, b.oddValue, type, [b])));
            }

            clearTimeout(timeoutId);
            if (!keepInCoupon) {
                setBetSlip(new Map());
                setSimpleStakes({});
                setCombinedStake('');
                if (onCloseMobile) onCloseMobile();
            }
            toast.success("¡Apuesta realizada con éxito!");

        } catch (e) {
            clearTimeout(timeoutId);
            console.error("Place Error:", e);
            toast.error(e.message || "Error al realizar apuesta");
        } finally {
            setIsPlacing(false);
        }
    };

    return (
        <aside
            className={isMobileModal ? mobileClasses : desktopClasses}
            style={styles}
        >
            {/* Header */}
            <div
                className={`bg-[#181818] px-4 py-3 flex flex-col shadow-xl z-20 shrink-0 border-b border-white/5 ${!isMobileModal ? 'cursor-pointer hover:bg-[#1f1f1f] transition-colors group' : ''}`}
                onClick={!isMobileModal ? toggleMinimize : undefined}
            >
                <div className="flex justify-between items-center w-full mb-3">
                    <div className="flex items-center gap-3">
                        <div className="bg-[#F59E0B] w-6 h-6 rounded-full flex items-center justify-center font-black text-black text-[10px] shadow-[0_0_15px_rgba(245,158,11,0.4)]">
                            {betSlip.size}
                        </div>
                        <h1 className="font-black text-xs uppercase tracking-[0.15em] text-white">Tu Cupón</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={(e) => { e.stopPropagation(); setBetSlip(new Map()); }} className="text-white/30 hover:text-white transition-colors p-1">
                            <Trash2 size={16} />
                        </button>
                        {!isMobileModal && (
                            <div className={`transition-transform duration-300 ${isMinimized ? 'rotate-180' : ''} text-white/30 group-hover:text-white p-1`}>
                                <ChevronDown size={18} />
                            </div>
                        )}
                        {isMobileModal && (
                            <button onClick={onCloseMobile} className="bg-white/5 rounded-full p-1.5 text-white/50">
                                <ChevronDown size={18} />
                            </button>
                        )}
                    </div>
                </div>

                {/* TABS SEGMENTED CONTROL - Compact */}
                {(!isMinimized || isMobileModal) && selections.length > 0 && (
                    <div className="flex bg-black/40 p-1 rounded-xl gap-1 w-full h-[36px]" onClick={e => e.stopPropagation()}>
                        {[
                            { id: 'simple', label: 'SIMPLE', disabled: false },
                            { id: 'combined', label: activeTab === 'combined' && hasBuilder ? 'CREAR APUESTA' : 'COMBINADA', disabled: false }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                disabled={tab.disabled}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex-1 text-[9px] md:text-[10px] font-black uppercase rounded-lg transition-all border-none cursor-pointer flex items-center justify-center
                                    ${activeTab === tab.id ? 'bg-[#F59E0B] text-black shadow-lg' : 'text-white/40 hover:text-white hover:bg-white/5'}
                                    ${tab.disabled ? 'opacity-20 cursor-not-allowed' : ''}
                                `}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* CONTENT BODY */}
            <div className={`flex flex-col flex-1 overflow-hidden bg-[#121212] transition-opacity duration-300 ${(!isMinimized || isMobileModal) ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>

                {/* Error Banner */}
                {hasErrors && (
                    <div className="bg-red-500/10 p-3 mx-4 mt-4 rounded-xl border border-red-500/20 flex items-start gap-3 animate-pulse">
                        <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
                        <div>
                            <p className="text-red-500 text-[10px] font-black uppercase tracking-wide">Conflicto Detectado</p>
                            <p className="text-red-400/70 text-[10px] leading-tight">
                                {isMixed ? "No combinable con Crear Apuesta." :
                                    isBuilderInvalid ? "Mínimo 2 selecciones para Crear Apuesta." :
                                        "Eventos duplicados."}
                            </p>
                        </div>
                    </div>
                )}

                {/* BET LIST */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2 no-scrollbar">
                    {selections.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-center opacity-30">
                            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-4">
                                <FileText size={24} className="text-white/50" />
                            </div>
                            <p className="text-xs font-black uppercase text-white/50 tracking-widest">Cupón Vacío</p>
                            <p className="text-[9px] text-white/30 mt-2 max-w-[150px]">Selecciona cuotas para apostar.</p>
                        </div>
                    ) : (
                        selections.map(bet => (
                            <BetItem
                                key={bet.selectionId}
                                bet={bet}
                                onRemove={removeBet}
                                onStakeChange={handleStakeChange}
                                stake={simpleStakes[bet.selectionId]}
                                isConflict={getIsConflict(bet)}
                                activeTab={activeTab}
                                currencySymbol={currencySymbol}
                                showInput={activeTab === 'simple' && !hasErrors}
                            />
                        ))
                    )}
                </div>

                {/* FOOTER ACTIONS - COMPACT LAYOUT */}
                <div className={`bg-[#151515] border-t border-white/5 shadow-[0_-20px_40px_rgba(0,0,0,0.5)] z-[50] p-4 space-y-3 shrink-0 ${isMobileModal ? 'pb-24 lg:pb-5' : ''}`}>
                    {selections.length > 0 ? (
                        <>
                            {/* Stats Row */}
                            <div className="flex justify-between items-end px-1">
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">
                                        {activeTab === 'combined' ? 'Cuota Total' : 'Apuesta Total'}
                                    </span>
                                    {activeTab === 'combined' ? (
                                        <span className="text-[#F59E0B] font-mono font-black text-xl leading-none tracking-tight">
                                            {selections.reduce((acc, s) => acc * s.oddValue, 1).toFixed(2)}
                                        </span>
                                    ) : (
                                        <span className="text-white font-mono font-bold text-base leading-none">
                                            {formatCurrency(selections.reduce((acc, s) => acc + (parseFloat(simpleStakes[s.selectionId]) || 0), 0))}
                                        </span>
                                    )}
                                </div>

                                <div className="flex flex-col items-end gap-0.5">
                                    <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">Ganancia Máx. Est.</span>
                                    <span className="text-[#F59E0B] font-black text-xl leading-none tracking-tight text-shadow-glow">
                                        {formatCurrency(potentialWin)}
                                    </span>
                                </div>
                            </div>

                            {/* Action Row - Input + Button */}
                            <div className="flex gap-3 h-12">
                                {/* INPUT GROUP for Combined */}
                                {activeTab === 'combined' && !hasErrors && (
                                    <div className="relative w-28 bg-[#202020] rounded-xl border border-white/10 focus-within:border-[#F59E0B] transition-all h-full group shrink-0 overflow-hidden">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10 font-black text-[#F59E0B] text-xs">
                                            {currencySymbol}
                                        </div>
                                        <input
                                            type="number"
                                            inputMode="decimal"
                                            value={combinedStake}
                                            onChange={(e) => setCombinedStake(e.target.value)}
                                            placeholder="200"
                                            className="w-full h-full bg-transparent border-none text-white font-black text-lg text-right pr-3 outline-none placeholder:text-white/10"
                                        />
                                    </div>
                                )}

                                <button
                                    onClick={handlePlace}
                                    disabled={isPlacing || hasErrors}
                                    className={`flex-1 h-full rounded-xl font-black text-xs uppercase tracking-[0.15em] shadow-lg transition-all active:scale-[0.98] border-none cursor-pointer flex justify-center items-center gap-2 group relative overflow-hidden
                                        ${isPlacing || hasErrors
                                            ? 'bg-neutral-800 text-neutral-500 border border-white/5 cursor-not-allowed'
                                            : 'bg-[#F59E0B] text-black hover:bg-[#ffb02e] hover:shadow-[0_0_30px_rgba(245,158,11,0.4)]'}
                                    `}
                                >
                                    {isPlacing ? (
                                        <>
                                            <div className="w-4 h-4 border-[2px] border-black/20 border-t-black rounded-full animate-spin"></div>
                                            <span>Procesando...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>{hasErrors ? 'APOSTAR' : 'APOSTAR'}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="p-2 text-center">
                            <span className="text-[9px] text-white/30 uppercase tracking-widest font-bold">Sin selecciones</span>
                        </div>
                    )}
                </div>
            </div>
        </aside>
    );
};

export const MobileBetSlipFAB = ({ count, odds, onClick }) => {
    if (count === 0) return null;
    return (
        <div className="fixed bottom-[85px] left-0 right-0 px-4 z-40 lg:hidden pointer-events-none animate-in fade-in slide-in-from-bottom-4 duration-300">
            <button
                onClick={onClick}
                className="w-full pointer-events-auto bg-[#F59E0B] text-black rounded-2xl h-12 pl-2 pr-2 shadow-[0_10px_30px_rgba(0,0,0,0.5)] flex justify-between items-center active:scale-[0.97] transition-transform border-none cursor-pointer group hover:bg-[#ffb02e]"
            >
                <div className="flex items-center gap-3 pl-3">
                    <div className="bg-black/20 w-7 h-7 rounded-full flex items-center justify-center font-black text-black text-xs">
                        {count}
                    </div>
                    <div className="flex flex-col items-start translate-y-[1px]">
                        <span className="text-[10px] font-black uppercase tracking-wide leading-none text-black">Tu Cupón</span>
                        <span className="text-[8px] font-bold uppercase tracking-widest text-black/60">Ver Detalles</span>
                    </div>
                </div>
                <div className="flex items-center gap-2 bg-black/10 px-3 py-1.5 rounded-xl backdrop-blur-sm">
                    <span className="text-[8px] font-bold text-black/50 uppercase tracking-wider mr-1">Cuota</span>
                    <span className="font-black text-base tracking-tight text-black font-mono">{parseFloat(odds).toFixed(2)}</span>
                </div>
            </button>
        </div>
    );
};
